import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { Download, Edit, Share2 } from "lucide-react";
import jsPDF from "jspdf";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import {
  reviewScreenButtonWithColor,
  paddingAndMargin,
  fontFamily,
  paginationstyle,
  screenHeaderStyle,
  searchBoxStyle,
  useSharedValue,
} from "../Constants/constants";
import { formatDate } from "../../Utility/utility";
import LoaderIndicator from "../../Utility/loading";
import { ConditionalPagination } from "../../utils/paginationUtils";
import { formatAmount, PRICE_PER_PAGE_POUNDS, deleteInvoice } from "./InvoiceService";
import "./Invoice.css";

const columns = [
  { field: "invoiceNumber", headerName: "Invoice #" },
  { field: "organizationName", headerName: "Organization" },
  { field: "invoiceDate", headerName: "Invoice Date" },
  { field: "startDate", headerName: "Period Start" },
  { field: "endDate", headerName: "Period End" },
  { field: "totalPages", headerName: "Total Pages" },
  { field: "totalAmount", headerName: "Amount (£)" },
  { field: "status", headerName: "Status" },
  { field: "functions", headerName: "" },
];

export default function Invoice() {
  const [searchValue, setSearchValue] = useState("");
  const [isInputActive, setInputActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [estimatedAmount, setEstimatedAmount] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any>(null);
  const [emailIds, setEmailIds] = useState("");

  const { isMenuOpen, defaultOrganization } = useSharedValue();

  const [startIndex, setStartIndex] = useState(0);
  const [currentPage, setCurrtPage] = useState(1);
  const [endIndex, setEndIndex] = useState(10);
  const { numberOfSelectedRows } = useSharedValue();
  const [rowsPerPage] = useState(10);
  const [dateFilterStart, setDateFilterStart] = useState("");
  const [dateFilterEnd, setDateFilterEnd] = useState("");

  // Mock invoice data
  const generateMockInvoices = () => {
    const mockData = [];
    const organizations = defaultOrganization
      ? [{ id: defaultOrganization.id, name: defaultOrganization.name }]
      : [
          { id: "1", name: "Acme Corporation" },
          { id: "2", name: "Tech Solutions Ltd" },
          { id: "3", name: "Global Enterprises" },
          { id: "4", name: "Innovation Labs" },
          { id: "5", name: "Finance Partners" },
        ];

    const statuses = ["paid", "pending", "overdue"];

    // Generate 15 mock invoices
    for (let i = 0; i < 15; i++) {
      const org = organizations[i % organizations.length];
      const daysAgo = Math.floor(Math.random() * 90); // Random date within last 90 days
      const invoiceDate = new Date();
      invoiceDate.setDate(invoiceDate.getDate() - daysAgo);

      const startDateObj = new Date(invoiceDate);
      startDateObj.setDate(startDateObj.getDate() - 30);

      const totalPagesUsed = Math.floor(Math.random() * 5000) + 100;
      const amount = totalPagesUsed * PRICE_PER_PAGE_POUNDS;

      mockData.push({
        id: `INV-${String(i + 1000).padStart(4, "0")}`,
        organizationId: org.id,
        organizationName: org.name,
        invoiceNumber: `INV-${String(i + 1000).padStart(4, "0")}`,
        invoiceDate: invoiceDate.toISOString(),
        startDate: startDateObj.toISOString(),
        endDate: invoiceDate.toISOString(),
        totalPages: totalPagesUsed,
        pricePerPage: PRICE_PER_PAGE_POUNDS,
        totalAmount: amount,
        status: statuses[i % statuses.length],
        pdfUrl: null,
        createdBy: "System Admin",
        createdDate: invoiceDate.toISOString(),
      });
    }

    return mockData.sort(
      (a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime()
    );
  };

  // Load mock data on component mount
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockInvoices = generateMockInvoices();
      setRows(mockInvoices);
      setLoading(false);
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrganization]);

  const handleGenerateInvoice = async () => {
    if (!defaultOrganization?.id) {
      alert("Please select an organization first.");
      return;
    }

    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert("Start date cannot be after end date.");
      return;
    }

    try {
      setLoading(true);
      // Backend not ready yet - this would call generateInvoice API
      // await generateInvoice(defaultOrganization.id, startDate, endDate);
      alert("Invoice generation will be available when backend is ready");
      setIsGenerateModalOpen(false);
      setStartDate("");
      setEndDate("");
      setTotalPages(0);
      setEstimatedAmount(0);
      // Refresh would happen here when backend is ready
    } catch (error) {
      console.error("Error generating invoice:", error);
      alert("Invoice generation will be available once backend is implemented.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoiceId: string) => {
    try {
      // Generate sample invoice PDF
      const invoice = rows.find((r) => r.id === invoiceId);
      if (!invoice) return;

      // Create PDF using jsPDF
      const doc = new jsPDF();

      // Set font
      doc.setFont("helvetica");

      // Header
      doc.setFontSize(24);
      doc.setTextColor(42, 167, 224);
      doc.text("INVOICE", 105, 20, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Invoice #: ${invoice.invoiceNumber}`, 105, 30, { align: "center" });

      // Organization and Date
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Organization:", 20, 50);
      doc.setFont("helvetica", "normal");
      doc.text(invoice.organizationName, 55, 50);

      doc.setFont("helvetica", "bold");
      doc.text("Date:", 130, 50);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(invoice.invoiceDate), 145, 50);

      // Period
      doc.setFont("helvetica", "bold");
      doc.text("Period:", 20, 60);
      doc.setFont("helvetica", "normal");
      doc.text(`${formatDate(invoice.startDate)} - ${formatDate(invoice.endDate)}`, 40, 60);

      // Status
      doc.setFont("helvetica", "bold");
      doc.text("Status:", 130, 60);
      doc.setFont("helvetica", "normal");
      doc.text(invoice.status.toUpperCase(), 150, 60);

      // Table Header
      doc.setFillColor(42, 167, 224);
      doc.rect(20, 75, 170, 10, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text("Description", 25, 82);
      doc.text("Quantity", 80, 82);
      doc.text("Price/Unit", 120, 82);
      doc.text("Amount", 160, 82);

      // Table Row
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.text("Document Processing", 25, 92);
      doc.text(`${invoice.totalPages} pages`, 80, 92);
      doc.text(formatAmount(PRICE_PER_PAGE_POUNDS), 120, 92);
      doc.text(formatAmount(invoice.totalAmount), 160, 92);

      // Border for table
      doc.setDrawColor(221, 221, 221);
      doc.rect(20, 75, 170, 25);
      doc.line(20, 85, 190, 85);

      // Total
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Total Amount: ${formatAmount(invoice.totalAmount)}`, 190, 115, { align: "right" });

      // Footer
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(128, 128, 128);
      doc.text("Thank you for your business!", 105, 280, { align: "center" });

      // Save PDF
      doc.save(`${invoice.invoiceNumber}.pdf`);

      alert(`Invoice ${invoice.invoiceNumber} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Error generating invoice.");
    }
  };

  const handleEditInvoice = (invoice: any) => {
    setEditingInvoice(invoice);
    setEmailIds(invoice.emailIds || "");
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingInvoice) return;

    // Update the invoice in the rows
    const updatedRows = rows.map((row) => {
      if (row.id === editingInvoice.id) {
        return {
          ...row,
          invoiceNumber: editingInvoice.invoiceNumber,
          startDate: editingInvoice.startDate,
          endDate: editingInvoice.endDate,
          totalPages: editingInvoice.totalPages,
          totalAmount: editingInvoice.totalAmount,
          status: editingInvoice.status,
          emailIds: emailIds,
        };
      }
      return row;
    });

    setRows(updatedRows);
    setIsEditModalOpen(false);
    setEditingInvoice(null);
    setEmailIds("");
    alert("Invoice updated successfully!");
  };

  const handleSendInvoice = (invoice: any) => {
    const emails = invoice.emailIds || "";
    if (!emails) {
      alert("Please add email addresses by editing the invoice first.");
      return;
    }
    alert(`Invoice ${invoice.invoiceNumber} will be sent to: ${emails}`);
    // Backend integration would happen here
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoiceId) return;

    if (!deleteReason.trim()) {
      alert("Please provide a reason for deletion.");
      return;
    }

    try {
      setLoading(true);
      await deleteInvoice(selectedInvoiceId);
      alert("Invoice deleted successfully!");
      setIsDeleteModalOpen(false);
      setSelectedInvoiceId(null);
      setDeleteReason("");
      // Refresh would happen here when backend is ready
    } catch (error) {
      console.error("Error deleting invoice:", error);
      alert("Invoice deletion will be available once backend is implemented.");
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const filteredRows = rows.filter((row) => {
    // Search filter
    const matchesSearch =
      !searchValue ||
      (row.invoiceNumber?.toLowerCase() || "").includes(searchValue.toLowerCase()) ||
      (row.organizationName?.toLowerCase() || "").includes(searchValue.toLowerCase()) ||
      (row.status?.toLowerCase() || "").includes(searchValue.toLowerCase());

    // Date range filter
    let matchesDateRange = true;
    if (dateFilterStart && dateFilterEnd) {
      const invoiceDate = new Date(row.invoiceDate);
      const filterStart = new Date(dateFilterStart);
      const filterEnd = new Date(dateFilterEnd);
      filterEnd.setHours(23, 59, 59, 999);
      matchesDateRange = invoiceDate >= filterStart && invoiceDate <= filterEnd;
    }

    return matchesSearch && matchesDateRange;
  });

  const handlePageChange = (event: any, newPage: number) => {
    setCurrtPage(newPage);
    setStartIndex((parseInt(newPage.toString()) - 1) * numberOfSelectedRows);
    setEndIndex((parseInt(newPage.toString()) - 1) * numberOfSelectedRows + numberOfSelectedRows);
  };

  const totalSpent = filteredRows.reduce((sum, invoice) => sum + invoice.totalAmount, 0);
  const totalPagesUsed = filteredRows.reduce((sum, invoice) => sum + invoice.totalPages, 0);

  return (
    <div
      className="invoice-container"
      style={{ ...(screenHeaderStyle(isMenuOpen) as React.CSSProperties) }}
    >
      {/* Search and Filter Bar */}
      <div className="search-filter-bar" style={{ marginTop: paddingAndMargin.headerTopHeight }}>
        <div className="search-input-wrapper">
          <InputBase
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setInputActive(true)}
            onBlur={() => setInputActive(false)}
            className="search-input"
            style={searchBoxStyle}
            placeholder="Search Invoice..."
            inputProps={{ "aria-label": "Invoice Search", style: { padding: "0 12px" } }}
            endAdornment={
              isInputActive || searchValue ? (
                <CloseIcon
                  style={{ cursor: "pointer", color: "#2aa7e0", marginRight: "8px" }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setSearchValue("");
                    setInputActive(false);
                  }}
                />
              ) : (
                <SearchIcon style={{ color: "#2aa7e0", marginRight: "8px" }} />
              )
            }
          />
          <Button
            disableElevation
            variant="contained"
            style={reviewScreenButtonWithColor(130) as React.CSSProperties}
            onClick={() => setIsGenerateModalOpen(true)}
          >
            Create Invoice
          </Button>

          <Button
            disableElevation
            variant="contained"
            style={reviewScreenButtonWithColor(130) as React.CSSProperties}
          >
            Add Invoice
          </Button>
        </div>

        <div className="date-filter-group">
          <input
            type="date"
            placeholder="DD/MM/YYYY"
            className="edit-field-input"
            value={dateFilterStart}
            onChange={(e) => setDateFilterStart(e.target.value)}
          />
          <span className="date-separator">to</span>
          <input
            type="date"
            className="edit-field-input"
            value={dateFilterEnd}
            onChange={(e) => setDateFilterEnd(e.target.value)}
          />
          {(dateFilterStart || dateFilterEnd) && (
            <Button
              onClick={() => {
                setDateFilterStart("");
                setDateFilterEnd("");
              }}
              size="small"
              className="clear-date-button"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards-container">
        {/* Total Invoices Card */}
        <div className="summary-card invoices">
          <div className="summary-card-header">
            <div className="summary-card-title" style={{ fontFamily }}>
              Total Invoices
            </div>
            <div className="summary-card-icon invoices">
              <span>📄</span>
            </div>
          </div>
          <div className="summary-card-value invoices" style={{ fontFamily }}>
            {filteredRows.length}
          </div>
          <div className="summary-card-subtitle" style={{ fontFamily }}>
            {dateFilterStart && dateFilterEnd ? "Filtered results" : "All time"}
          </div>
        </div>

        {/* Total Pages Card */}
        <div className="summary-card pages">
          <div className="summary-card-header">
            <div className="summary-card-title" style={{ fontFamily }}>
              Total Pages
            </div>
            <div className="summary-card-icon pages">
              <span>📊</span>
            </div>
          </div>
          <div className="summary-card-value pages" style={{ fontFamily }}>
            {totalPagesUsed.toLocaleString()}
          </div>
          <div className="summary-card-subtitle" style={{ fontFamily }}>
            Pages processed
          </div>
        </div>

        {/* Total Spent Card */}
        <div className="summary-card spent">
          <div className="summary-card-header">
            <div className="summary-card-title" style={{ fontFamily }}>
              Total Spent
            </div>
            <div className="summary-card-icon spent">
              <span>💰</span>
            </div>
          </div>
          <div className="summary-card-value spent" style={{ fontFamily }}>
            {formatAmount(totalSpent)}
          </div>
          <div className="summary-card-subtitle" style={{ fontFamily }}>
            @ {formatAmount(PRICE_PER_PAGE_POUNDS)} per page
          </div>
        </div>
      </div>

      {/* Column Titles */}
      <div className="table-header" style={{ fontFamily }}>
        {columns.map((column) => (
          <div key={column.field} className="table-header-column" style={{ fontFamily }}>
            {column.headerName}
          </div>
        ))}
      </div>

      {/* Main container for table with fixed height */}
      <div className="table-main-container">
        {/* Scrollable rows container */}
        <div className="table-scrollable-container">
          {loading ? (
            <div className="loading-container">
              <LoaderIndicator />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <div className="empty-state-title">No invoices found</div>
              <div className="empty-state-subtitle">
                {dateFilterStart || dateFilterEnd
                  ? "Try adjusting your date filter"
                  : "Invoice history will appear here"}
              </div>
            </div>
          ) : (
            <div>
              {/* Row Data */}
              {filteredRows.slice(startIndex, endIndex).map((row, index) => (
                <div key={row.id} className={`table-row ${index % 2 === 0 ? "even" : "odd"}`}>
                  {columns.map((column) => (
                    <div
                      title={
                        column.field === "totalAmount"
                          ? formatAmount(row[column.field])
                          : String(row[column.field] || "")
                      }
                      key={column.field}
                      className={`table-cell ${column.field === "invoiceNumber" ? "invoice-number" : ""}`}
                      style={{ fontFamily }}
                    >
                      {column.field === "functions" ? (
                        <div className="functions-cell">
                          <div
                            onClick={() => handleDownloadPDF(row.id)}
                            className="download-button"
                            title="Download Invoice"
                          >
                            <Download className="download-icon" size={18} />
                          </div>
                          <div
                            onClick={() => handleEditInvoice(row)}
                            className="edit-button"
                            title="Edit Invoice"
                          >
                            <Edit className="edit-icon" size={18} />
                          </div>
                          <div
                            onClick={() => handleSendInvoice(row)}
                            className="edit-button"
                            title="Send Invoice"
                          >
                            <Share2 className="edit-icon" size={18} />
                          </div>
                          {/* <div
                            onClick={() => handleSendInvoice(row)}
                            className="send-button"
                            title="Send Invoice"
                          >
                            <Share2 className="send-icon" size={18} />
                          </div> */}
                          {/* <DeleteButton
                            onClick={() => {
                              setSelectedInvoiceId(row.id);
                              setIsDeleteModalOpen(true);
                            }}
                          /> */}
                        </div>
                      ) : column.field === "status" ? (
                        <span className={`status-badge ${row.status}`}>{row.status}</span>
                      ) : column.field === "invoiceDate" ||
                        column.field === "startDate" ||
                        column.field === "endDate" ? (
                        formatDate(row[column.field])
                      ) : column.field === "totalAmount" ? (
                        <span className="table-cell amount">{formatAmount(row[column.field])}</span>
                      ) : column.field === "totalPages" ? (
                        <span className="table-cell pages">
                          {row[column.field].toLocaleString()}
                        </span>
                      ) : (
                        String(row[column.field] || "")
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Generate Invoice Modal */}
      <Modal open={isGenerateModalOpen} onClose={() => setIsGenerateModalOpen(false)}>
        <div className="modal-container">
          <h6 className="modal-title" style={{ fontFamily }}>
            GENERATE INVOICE
          </h6>
          <div className="modal-divider"></div>

          {defaultOrganization && (
            <div className="modal-organization" style={{ fontFamily }}>
              <strong>Organization:</strong> {defaultOrganization.name}
            </div>
          )}

          <div className="modal-date-inputs">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              className="modal-date-input"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>

          {totalPages > 0 && (
            <div className="modal-estimate-box" style={{ fontFamily }}>
              <div className="modal-estimate-row">
                <strong>Total Pages:</strong> {totalPages}
              </div>
              <div className="modal-estimate-row">
                <strong>Price per Page:</strong> {formatAmount(PRICE_PER_PAGE_POUNDS)}
              </div>
              <div className="modal-estimate-total">
                <strong>Estimated Amount:</strong> {formatAmount(estimatedAmount)}
              </div>
            </div>
          )}

          <div className="modal-buttons">
            <Button
              disableElevation
              variant="contained"
              className="modal-button primary"
              style={{ fontFamily }}
              onClick={handleGenerateInvoice}
              disabled={!startDate || !endDate || totalPages === 0}
            >
              Generate
            </Button>
            <Button
              disableElevation
              variant="contained"
              className="modal-button secondary"
              style={{ fontFamily }}
              onClick={() => {
                setIsGenerateModalOpen(false);
                setStartDate("");
                setEndDate("");
                setTotalPages(0);
                setEstimatedAmount(0);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <div className="modal-container edit">
          <h6 className="modal-title" style={{ fontFamily }}>
            EDIT INVOICE
          </h6>
          <div className="modal-divider"></div>

          {editingInvoice && (
            <div className="modal-edit-form" style={{ fontFamily }}>
              <div className="edit-field-row">
                <label className="edit-field-label">Invoice Number</label>
                <input
                  type="text"
                  className="edit-field-input"
                  value={editingInvoice.invoiceNumber}
                  onChange={(e) =>
                    setEditingInvoice({ ...editingInvoice, invoiceNumber: e.target.value })
                  }
                />
              </div>

              <div className="edit-field-row">
                <label className="edit-field-label">Period Start</label>
                <input
                  type="date"
                  className="edit-field-input"
                  value={editingInvoice.startDate?.split("T")[0] || ""}
                  onChange={(e) =>
                    setEditingInvoice({ ...editingInvoice, startDate: e.target.value })
                  }
                />
              </div>

              <div className="edit-field-row">
                <label className="edit-field-label">Period End</label>
                <input
                  type="date"
                  className="edit-field-input"
                  value={editingInvoice.endDate?.split("T")[0] || ""}
                  onChange={(e) =>
                    setEditingInvoice({ ...editingInvoice, endDate: e.target.value })
                  }
                />
              </div>

              <div className="edit-field-row">
                <label className="edit-field-label">Total Pages</label>
                <input
                  type="number"
                  className="edit-field-input"
                  value={editingInvoice.totalPages}
                  onChange={(e) => {
                    const pages = parseInt(e.target.value) || 0;
                    setEditingInvoice({
                      ...editingInvoice,
                      totalPages: pages,
                      totalAmount: pages * PRICE_PER_PAGE_POUNDS,
                    });
                  }}
                />
              </div>

              <div className="edit-field-row">
                <label className="edit-field-label">Amount (£)</label>
                <input
                  type="standard"
                  className="edit-field-input"
                  value={editingInvoice.totalAmount}
                  onChange={(e) =>
                    setEditingInvoice({
                      ...editingInvoice,
                      totalAmount: parseFloat(e.target.value) || 0,
                    })
                  }
                  //   readOnly
                />
              </div>

              <div className="edit-field-row">
                <label className="edit-field-label">Status</label>
                <select
                  className="edit-field-input"
                  value={editingInvoice.status}
                  onChange={(e) => setEditingInvoice({ ...editingInvoice, status: e.target.value })}
                >
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>

              <div className="edit-field-row">
                <label className="edit-field-label">Email IDs</label>
                <input
                  type="text"
                  className="edit-field-input"
                  value={emailIds}
                  onChange={(e) => setEmailIds(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                />
              </div>
            </div>
          )}

          <div className="modal-buttons">
            <Button
              disableElevation
              variant="contained"
              style={reviewScreenButtonWithColor(130) as React.CSSProperties}
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingInvoice(null);
                setEmailIds("");
              }}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              variant="contained"
              className="modal-button primary"
              style={reviewScreenButtonWithColor(130) as React.CSSProperties}
              onClick={handleSaveEdit}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
        <div className="modal-container delete">
          <h6 className="modal-title" style={{ fontFamily }}>
            DELETE CONFIRMATION
          </h6>
          <div className="modal-divider"></div>
          <div className="modal-delete-message" style={{ fontFamily }}>
            Are you sure you want to delete this invoice?
          </div>
          <div className="modal-delete-message" style={{ fontFamily }}>
            <label htmlFor="delete-reason" className="modal-delete-input-label">
              Reason for deletion:
            </label>
            <input
              id="delete-reason"
              type="text"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              className="modal-delete-input"
              placeholder="Enter reason (required)"
              required
            />
          </div>
          <div className="modal-buttons">
            <Button
              disableElevation
              variant="contained"
              className="modal-button primary"
              style={{ fontFamily }}
              onClick={handleDeleteInvoice}
              disabled={!deleteReason.trim()}
            >
              Delete
            </Button>
            <Button
              disableElevation
              variant="contained"
              className="modal-button secondary"
              style={{ fontFamily }}
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteReason("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <ConditionalPagination
        itemCount={rows.length}
        itemsPerPage={rowsPerPage}
        threshold={rowsPerPage}
        paginationProps={{
          page: currentPage,
          onChange: handlePageChange,
          style: paginationstyle,
        }}
      />
    </div>
  );
}
